import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import MainTitle from "../../components/mainTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import { pages, logger } from "../../../utils/common";
import {
  useDeleteMaterialItemHook,
  useMaterialGroupByIdHook,
} from "../../../hooks/useMaterialHook";
import {
  formatLabel,
  renderMaterialStatusChip,
} from "./material.helpers";
import { MaterialGroupDetailViewProps } from "./material.interfaces";
import ManageMaterialGroupDialog from "./manageMaterialGroupDialog";
import ManageMaterialItemDialog from "./manageMaterialItemDialog";
import { TMaterialItem } from "../../../apis/material/material.interfaces";

const MetaItem: FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">
      {value === undefined || value === null || value === "" ? "—" : value}
    </Typography>
  </Grid>
);

const MaterialGroupDetailView: FC<MaterialGroupDetailViewProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: group, isLoading, error } = useMaterialGroupByIdHook(id);
  const deleteItemMutation = useDeleteMaterialItemHook();

  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TMaterialItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TMaterialItem | null>(
    null,
  );

  const goBack = () => navigate(pages.material.path);

  const openAddItem = () => {
    setEditingItem(null);
    setItemDialogOpen(true);
  };
  const openEditItem = (item: TMaterialItem) => {
    setEditingItem(item);
    setItemDialogOpen(true);
  };
  const closeItemDialog = () => {
    setItemDialogOpen(false);
    setEditingItem(null);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?.id || !group) return;
    try {
      await deleteItemMutation.mutateAsync({
        materialGroupId: group.id,
        itemId: pendingDelete.id,
      });
      logger.show_success("Item deleted.");
    } catch (err) {
      logger.show_error(
        err instanceof Error ? err.message : "Failed to delete item.",
      );
    } finally {
      setPendingDelete(null);
    }
  };

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.material} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="material-detail-loading"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !group) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.material} />
        <Box p={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            data-testid="material-detail-back"
          >
            Back to Materials
          </Button>
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to load material group.
          </Alert>
        </Box>
      </>
    );
  }

  const items = group.items ?? [];

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.material} />
      <Grid container spacing={3} data-testid="material-detail-view">
        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={goBack}
                data-testid="material-detail-back"
              >
                Back
              </Button>
              <MainTitle title={group.name} />
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              {renderMaterialStatusChip(group.status)}
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditGroupOpen(true)}
                data-testid="edit-material-group-button"
              >
                Edit Group
              </Button>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="material-detail-meta">
            <CardContent>
              <Grid container spacing={2}>
                <MetaItem label="Category" value={formatLabel(group.category)} />
                <MetaItem
                  label="Condition"
                  value={formatLabel(group.condition)}
                />
                <MetaItem label="Department" value={group.department} />
                <MetaItem label="Location" value={group.location} />
                <MetaItem label="Quantity" value={group.quantity} />
                <MetaItem
                  label="Min Quantity"
                  value={group.minimumQuantity}
                />
                <MetaItem label="Supplier" value={group.supplier} />
                <MetaItem label="Unit Cost" value={group.cost} />
                <MetaItem
                  label="Purchase Date"
                  value={
                    group.purchaseDate
                      ? new Date(group.purchaseDate).toLocaleDateString()
                      : undefined
                  }
                />
                <MetaItem
                  label="Last Maintenance"
                  value={
                    group.lastMaintenanceDate
                      ? new Date(
                          group.lastMaintenanceDate,
                        ).toLocaleDateString()
                      : undefined
                  }
                />
                <MetaItem
                  label="Created"
                  value={new Date(group.createdAt).toLocaleString()}
                />
                <MetaItem
                  label="Updated"
                  value={new Date(group.updatedAt).toLocaleString()}
                />
              </Grid>

              {group.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.description}
                  </Typography>
                </>
              )}

              {group.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.notes}
                  </Typography>
                </>
              )}

              {group.tags && group.tags.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Tags
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {group.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="material-detail-items">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Items</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openAddItem}
                  data-testid="add-material-item-button"
                >
                  Add Item
                </Button>
              </Stack>
              {items.length === 0 ? (
                <Alert severity="info" data-testid="material-no-items">
                  No items have been added to this material group yet.
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  data-testid="material-items-table-container"
                >
                  <Table size="small" data-testid="material-items-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, idx) => (
                        <TableRow
                          key={item.id ?? idx}
                          hover
                          data-testid={`material-item-row-${item.id ?? idx}`}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatLabel(item.item_type)}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell>
                            {renderMaterialStatusChip(item.status)}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                maxWidth: 320,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.description ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => openEditItem(item)}
                              data-testid={`edit-material-item-${item.id ?? idx}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setPendingDelete(item)}
                              data-testid={`delete-material-item-${item.id ?? idx}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ManageMaterialGroupDialog
        open={editGroupOpen}
        onClose={() => setEditGroupOpen(false)}
        group={group}
      />

      <ManageMaterialItemDialog
        open={itemDialogOpen}
        onClose={closeItemDialog}
        materialGroupId={group.id}
        item={editingItem}
      />

      <Dialog
        open={!!pendingDelete}
        onClose={() =>
          deleteItemMutation.isPending ? undefined : setPendingDelete(null)
        }
        data-testid="delete-material-item-confirm"
      >
        <DialogTitle>Delete item?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove &ldquo;{pendingDelete?.name ?? "this item"}&rdquo; from{" "}
            {group.name}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDelete(null)}
            disabled={deleteItemMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteItemMutation.isPending}
            startIcon={
              deleteItemMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-material-item-button"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MaterialGroupDetailView;
